import { Badge, Button, Callout, Card, PageHeader, Spinner, buttonClass, cx } from "@gmx/ui";
import { CircleCheck, CirclePlus, Download, Pin, RefreshCw, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router";
import { ExtensionGlyph, IgGlyph } from "../components/IgGlyph";
import { useAuth } from "../lib/auth";
import { useDocumentTitle } from "../lib/hooks";
import { useIgAccount } from "../lib/igAccount";
import { useExtensionStatus } from "../lib/sso";
import { InboxPrivacyCard } from "../components/InboxPrivacyCard";

export function ConnectExtensionPage() {
  useDocumentTitle("Extension");
  const ext = useExtensionStatus();
  const { isImpersonating } = useAuth();
  const { accounts } = useIgAccount();

  // Opening this page is an explicit "connect me": re-ping, and hand over a token if it's installed but signed out.
  useEffect(() => {
    ext.recheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = ext.status;
  const headline =
    status === "connected"
      ? "You're connected."
      : status === "signed_out"
        ? "Installed, but signed out."
        : status === "checking"
          ? "Looking for the extension…"
          : "Extension not detected.";

  const steps = [
    {
      icon: <Download size={18} />,
      title: "Install Grow My X for Chrome",
      text: "Download it from Get the extension and load it in two minutes (Chrome Web Store version coming soon). Works in Chrome, Edge, Brave and Arc.",
      done: ext.detected,
    },
    {
      icon: <Pin size={18} />,
      title: "Pin it to your toolbar",
      text: "Click the puzzle piece next to the address bar, then the pin next to Grow My X, so you can see its status at a glance.",
      done: ext.detected,
    },
    {
      icon: <ExtensionGlyph size={18} />,
      title: "Connect it to this account",
      text: "Come back to this page. It signs the extension in with your Grow My X account automatically, no second login.",
      done: ext.connected,
    },
    {
      icon: <IgGlyph size={18} />,
      title: "Open X",
      text: "Log into x.com in this browser with the X account you want Grow My X to run.",
      done: accounts.length > 0,
    },
    {
      icon: <CirclePlus size={18} />,
      title: "Connect the account in the extension",
      text: "Click the Grow My X icon in your toolbar and press “Connect @yourhandle”. Nothing runs until you do. It shows up here within a minute.",
      done: accounts.length > 0,
    },
  ];

  return (
    <div className="gfp-page gfp-connect">
      <PageHeader
        title="The extension"
        description="Grow My X's extension does the work inside X, from your own browser and your own login. This portal is mission control."
      />

      <Card accent className={cx("gfp-connect__status", `gfp-connect__status--${status}`)}>
        <div className="gfp-connect__statusicon" aria-hidden="true">
          {status === "checking" ? <Spinner size={22} label={null} /> : status === "connected" ? <CircleCheck size={24} /> : <ExtensionGlyph size={24} />}
        </div>
        <div className="gfp-connect__statusbody" aria-live="polite">
          <h2 className="gfp-connect__headline">{headline}</h2>
          <p className="gfp-connect__statustext">
            {status === "connected"
              ? "The extension is signed in to your Grow My X account in this browser."
              : status === "signed_out"
                ? "We found the extension in this browser, but it isn't signed in yet."
                : status === "checking"
                  ? "Checking this browser for Grow My X."
                  : "We can't see the extension in this browser. Install it, then check again."}
          </p>
          {ext.version ? (
            <Badge size="sm" className="gfp-connect__version">
              Version {ext.version}
            </Badge>
          ) : null}
        </div>
        <div className="gfp-connect__actions">
          {status === "signed_out" || status === "connected" ? (
            <Button
              variant={status === "connected" ? "secondary" : "primary"}
              loading={ext.syncing}
              disabled={isImpersonating}
              onClick={() => void ext.connect()}
            >
              {status === "connected" ? "Reconnect" : "Connect now"}
            </Button>
          ) : null}
          {status === "not_detected" ? (
            <Button variant="secondary" icon={<RefreshCw size={16} />} onClick={() => ext.recheck()}>
              Check again
            </Button>
          ) : null}
        </div>
      </Card>

      {isImpersonating ? (
        <Callout tone="warn" title="You're viewing someone else's account">
          The extension stays signed in as you while you impersonate a customer, so it never runs their automations on your
          X.
        </Callout>
      ) : null}

      <InboxPrivacyCard />

      <div className="gfp-connect__grid">
        <section className="gfp-connect__steps" aria-labelledby="gfp-steps-title">
          <h2 id="gfp-steps-title" className="gfp-section-title">
            Setup takes three minutes
          </h2>
          <ol className="gfp-steps">
            {steps.map((s, i) => (
              <li key={s.title} className={cx("gfp-steps__item", s.done && "gfp-steps__item--done")}>
                <span className="gfp-steps__num" aria-hidden="true">
                  {s.done ? <CircleCheck size={18} /> : i + 1}
                </span>
                <div className="gfp-steps__body">
                  <h3 className="gfp-steps__title">
                    {s.title}
                    {s.done ? <span className="gf-sr-only"> (done)</span> : null}
                  </h3>
                  <p className="gfp-steps__text">{s.text}</p>
                  {i === 0 && !s.done ? (
                    <Link to="/extension" className={buttonClass({ variant: "primary", size: "sm", className: "gfp-steps__cta" })}>
                      <Download size={15} aria-hidden="true" /> Download the extension
                    </Link>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </section>

        <aside className="gfp-connect__faq">
          <Card title="Straight answers" padding="md">
            <dl className="gfp-faq">
              <div>
                <dt>
                  <ShieldCheck size={16} aria-hidden="true" /> Does it need my X password?
                </dt>
                <dd>No. It uses the X session already open in your browser. Your password never touches Grow My X.</dd>
              </div>
              <div>
                <dt>Does my computer have to be on?</dt>
                <dd>Workflows run while Chrome and an authenticated X tab are available. Keep your computer awake for scheduled work.</dd>
              </div>
              <div>
                <dt>Can I run more than one X account?</dt>
                <dd>
                  Yes, up to your plan's limit. Give each account its own Chrome profile with the extension installed and signed
                  in, then click Connect in each one. Accounts you've already connected are picked up automatically wherever
                  they're logged in, and only one browser runs an account at a time.{" "}
                  <Link to="/settings?tab=instagram" className="gf-link">
                    Your accounts
                  </Link>
                </dd>
              </div>
              <div>
                <dt>Will X block me?</dt>
                <dd>
                  Grow My X paces every action under conservative daily caps and pauses itself the moment X pushes back. You
                  control the limits in Settings.
                </dd>
              </div>
            </dl>
          </Card>
        </aside>
      </div>
    </div>
  );
}
